import { json, postBiz, rows } from '../core/coreapi';
import { PpmSuperactivityWorkbenchModel } from './procurement.models';
import {
  normalizeLookup,
  normalizePpm,
  normalizePpmLine,
  normalizePpmReportRow,
  normalizePpmSuperactivity,
  normalizePpmSuperactivityWorkbench,
  normalizePpmWorkbench,
} from './procurement.normalizers';

export const fetchPpmWorkbench = async (
  planningcycleid: number,
  ppmid?: number | null
) =>
  normalizePpmWorkbench(
    json(
      await postBiz({
        s: 'ppmworkbench',
        a: 'workbench',
        pci: planningcycleid,
        rid: ppmid ?? null,
      })
    )
  );

export const fetchPpmLookups = async (planningcycleid: number) => {
  const d: any = json(
    await postBiz({ s: 'ppmworkbench', a: 'lookups', pci: planningcycleid })
  );
  return {
    planningcycles: (d?.planningcycles ?? []).map(normalizeLookup),
    budgetversions: (d?.budgetversions ?? d?.versions ?? []).map(
      normalizeLookup
    ),
    statuses: (d?.statuses ?? []).map(normalizeLookup),
    stages: (d?.stages ?? []).map(normalizeLookup),
    sections: (d?.sections ?? []).map(normalizeLookup),
    methods: (d?.methods ?? []).map(normalizeLookup),
    natures: (d?.natures ?? []).map(normalizeLookup),
    reviews: (d?.reviews ?? []).map(normalizeLookup),
  };
};

export const fetchPpms = async (planningcycleid: number, status = '') =>
  rows(
    await postBiz({
      s: 'ppm',
      a: 'find',
      pci: planningcycleid,
      pst: status || null,
      pgo: 0,
      pgl: 100,
    })
  ).map(normalizePpm);

export const fetchPpmLines = async (
  planningcycleid: number,
  ppmid: number,
  section = '',
  superactivityid: number | null = null
) =>
  rows(
    await postBiz({
      s: 'ppm',
      a: 'lines',
      pci: planningcycleid,
      rid: ppmid,
      psc: section || null,
      sai: superactivityid,
    })
  ).map(normalizePpmLine);

export const generateDraftPpm = (
  planningcycleid: number,
  name: string,
  sourcecode = 'CONSOLIDATION'
) =>
  json(
    postBiz({
      s: 'ppm',
      a: 'generate',
      pci: planningcycleid,
      src: sourcecode,
      nam: name,
    })
  );

export const savePpmLines = (
  planningcycleid: number,
  ppmid: number,
  records: any[]
) =>
  json(
    postBiz({
      s: 'ppm',
      a: 'bulksave',
      pci: planningcycleid,
      rid: ppmid,
      rcd: records,
    })
  );

export const calendarizePpm = (planningcycleid: number, ppmid: number) =>
  json(
    postBiz({
      s: 'ppmworkbench',
      a: 'calendarize',
      pci: planningcycleid,
      rid: ppmid,
    })
  );

export const transitionPpm = (
  planningcycleid: number,
  ppmid: number,
  actioncode: string,
  notes: string,
  stamp?: string | null
) =>
  json(
    postBiz({
      s: 'ppm',
      a: 'transition',
      pci: planningcycleid,
      rid: ppmid,
      act: actioncode,
      nts: notes,
      stp: stamp ?? null,
    })
  );

export const fetchOfficialPpmReport = (
  planningcycleid: number,
  ppmid: number
) =>
  json(
    postBiz({ s: 'ppmreport', a: 'official', pci: planningcycleid, rid: ppmid })
  );

// export const fetchOfficialPpmRows = async (
//   planningcycleid: number,
//   ppmid: number
// ) =>
//   rows(
//     await postBiz({
//       s: 'ppmreport',
//       a: 'rows',
//       pci: planningcycleid,
//       rid: ppmid,
//     })
//   ).map(normalizePpmLine);

export const fetchOfficialPpmRows = async (
  planningcycleid: number,
  ppmid: number
) =>
  rows(
    await postBiz({
      s: 'ppmreport',
      a: 'rows',
      pci: planningcycleid,
      rid: ppmid,
    })
  ).map(normalizePpmReportRow);

export const fetchOfficialPpmSummary = async (
  planningcycleid: number,
  ppmid: number
) =>
  rows(
    await postBiz({
      s: 'ppmreport',
      a: 'summary',
      pci: planningcycleid,
      rid: ppmid,
    })
  );


export const createPpmSuperactivity = (
  ppmid: number,
  name: string,
  code: string,
  description: string,
  section: string,
  method: string,
  nature: string,
  reviewtype: string
) =>
  json(
    postBiz({
      s: 'ppmsuperactivity',
      a: 'create',
      ppi: ppmid,
      nam: name,
      shc: code,
      dsc: description,
      psc: section,
      pmd: method,
      pnt: nature,
      rvt: reviewtype,
    })
  );

export const fetchPpmSuperactivityWorkbench = async (
  superactivityid: number
): Promise<PpmSuperactivityWorkbenchModel> => {
  const response = await postBiz({
    s: 'ppmsuperactivity',
    a: 'workbench',
    rid: superactivityid,
  });

  return normalizePpmSuperactivityWorkbench(
    json(response)
  );
};

export const assignPpmSuperactivityLines = (
  superactivityid: number,
  lineids: number[]
) =>
  json(
    postBiz({
      s: 'ppmsuperactivity',
      a: 'assign',
      rid: superactivityid,
      ids: lineids,
    })
  );